import type { ReactNode } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/Popover'
import { useAppSelector } from '@/store/store-hooks'
import { SupportedFormatsContent } from '@/user_files/drive/components/supported-formats/SupportedFormatsContent'
import { selectFileById } from '../store/selectors'
import { FileUploadState } from '../store/slice'
import { UserFileProcessingBlockedReason, UserFileProcessingStatus } from '@/user_files/schemas/processing-status'
import { UploadCard } from '@/components/upload-card/UploadCard'
import { UploadCardStatus } from '@/components/upload-card/constants'
import { getUserFilesUploadFilenameGuidance } from '@/user_files/utils/upload-validation'

const DISMISSIBLE_STATUSES = new Set<UploadCardStatus>([
  UploadCardStatus.Complete,
  UploadCardStatus.Error,
  UploadCardStatus.Processing,
  UploadCardStatus.Queued,
])

type ProcessingStatusItemProps = {
  clientTmpId: string
  statusPrefix?: string
  onCancel: (clientTmpId: string) => void
  onDismiss: (clientTmpId: string) => void
}

function mapFileToUploadCardStatus({ client, backend }: FileUploadState): UploadCardStatus {
  const processingStatus = backend?.record.processing_status

  if (client.uploadStatus === 'unsupported_type') {
    return UploadCardStatus.Error
  }

  if (client.uploadStatus === 'validating') {
    return UploadCardStatus.UploadPending
  }

  if (client.uploadStatus === 'queued') {
    return UploadCardStatus.UploadPending
  }

  if (client.uploadStatus === 'initializing') {
    return UploadCardStatus.UploadPending
  }

  if (client.uploadStatus === 'uploading') {
    return UploadCardStatus.Uploading
  }

  if (client.uploadStatus === 'error') {
    return UploadCardStatus.Error
  }

  if (client.uploadStatus === 'uploaded' && processingStatus == null) {
    return UploadCardStatus.Pending
  }

  if (processingStatus === UserFileProcessingStatus.Completed) {
    return UploadCardStatus.Complete
  }

  if (processingStatus === UserFileProcessingStatus.Failed) {
    return UploadCardStatus.Error
  }

  if (backend?.record.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota) {
    return UploadCardStatus.Queued
  }

  if (processingStatus === UserFileProcessingStatus.Pending) {
    return UploadCardStatus.Pending
  }

  return UploadCardStatus.Processing
}

function getUploadCardMessage({ client, backend }: FileUploadState): string | ReactNode | undefined {
  const processingStatus = backend?.record.processing_status

  if (client.uploadStatus === 'unsupported_type') {
    const filenameGuidance = getUserFilesUploadFilenameGuidance(client.filename)
    if (filenameGuidance) return filenameGuidance

    return (
      <>
        This file type is not currently supported.{' '}
        <Popover>
          <PopoverTrigger
            type="button"
            className="inline p-0 font-medium text-xs leading-4 text-brand-neutral-950 underline underline-offset-2 outline-hidden hover:text-brand-neutral-800 focus-visible:ring-2 focus-visible:ring-brand-500 rounded-xs"
          >
            View supported formats
          </PopoverTrigger>
          <PopoverContent side="top" sideOffset={8} width="auto" showCloseButton={false} align="start" className="max-w-[min(385px,calc(100vw-3rem))]">
            <div className="text-sm leading-5 tracking-[-0.15px] text-brand-neutral-950">
              <SupportedFormatsContent />
            </div>
          </PopoverContent>
        </Popover>
      </>
    )
  }

  if (client.uploadStatus === 'error') {
    return client.uploadError ?? 'Upload failed'
  }

  if (processingStatus === UserFileProcessingStatus.Failed) {
    return backend?.record.processing_error ?? 'Processing failed'
  }

  const isTerminal = processingStatus === UserFileProcessingStatus.Completed || processingStatus === UserFileProcessingStatus.Failed
  if (!isTerminal && backend?.record.processing_blocked_reason === UserFileProcessingBlockedReason.OcrQuota) {
    return 'Waiting for OCR page quota'
  }

  return undefined
}

function canDismiss(status: UploadCardStatus): boolean {
  return DISMISSIBLE_STATUSES.has(status)
}

/**
 * Individual file status item that selects its own data from Redux and renders an UploadCard.
 * Only re-renders when this specific file's data changes.
 */
export function ProcessingStatusItem({ clientTmpId, statusPrefix, onCancel, onDismiss }: ProcessingStatusItemProps) {
  const file = useAppSelector((state) => selectFileById(state, clientTmpId))

  if (!file) return null

  const { client, backend } = file
  const filename = backend?.record.original_filename ?? client.filename

  const status = mapFileToUploadCardStatus(file)
  const message = getUploadCardMessage(file)

  return (
    <UploadCard
      status={status}
      filename={filename}
      message={message}
      statusPrefix={statusPrefix}
      /** Show Cancel button while upload is pending or in progress */
      onCancel={status === UploadCardStatus.UploadPending || status === UploadCardStatus.Uploading ? () => onCancel(client.clientTmpId) : undefined}
      /** Dismissing a queued row only hides it; the file remains queued on the server. */
      onDismiss={canDismiss(status) ? () => onDismiss(client.clientTmpId) : undefined}
    />
  )
}
